import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import DraftIcon from '@plato/ui-legacy/assets/icons/draft-icon';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { ListView } from '@plato/ui-legacy/components/list-views/list-view';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { PageHeader } from '@plato/ui-legacy/components/ui/page-header';
import { useQueryParam } from '@plato/ui-legacy/lib/hooks/use-query-param';
import { stringParam } from '@plato/ui-legacy/lib/query-params';
import { DataTable } from '@plato/ui/components/data-table';
import { PlusIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    salesDraftSortFieldSchema,
    type SalesDraftSortDirection,
    type SalesDraftSortField,
} from '../../../server/types/drafts-types.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useGenericListViewSorting } from '../../list-views/hooks/use-generic-list-view-sorting.tsx';
import { DraftOrderCreateDialog } from '../../sales-draft-new/views/create-draft-order-dialog.tsx';
import { DraftListFilters } from '../components/draft-list-filters.tsx';
import { useDraftListAppliedFilters } from '../hooks/use-draft-list-applied-filters.tsx';
import { useDraftListColumns } from '../hooks/use-draft-list-columns.tsx';

export function DraftListPage() {
    const { t } = useTranslation();
    const { transactionDraftAvailability } = useAppConfig();

    const columns = useDraftListColumns();
    const sendEvent = useAnalytics();

    const [filters, setFilters] = useDraftListAppliedFilters();

    const {
        data: draftListRaw,
        isLoading: isLoadingDraftList,
        isFetchingNextPage: isFetchingDraftsListNextPage,
        hasNextPage,
        fetchNextPage,
    } = api.platoUser.query.salesDraft.getSalesDraftList.getWithFilters.useInfiniteQuery(filters, {
        getNextPageParam: (lastPage) => lastPage.nextCursor,
    });

    const [deletedDraftId] = useQueryParam('deleted_draft_id', stringParam());

    const data = useMemo(() => {
        const items = draftListRaw ? draftListRaw.pages.flatMap((page) => page.items) : [];
        return items.filter((item) => item.draft_order_id !== deletedDraftId);
    }, [draftListRaw, deletedDraftId]);

    const [sorting, sortChangedInInterface] = useGenericListViewSorting<
        SalesDraftSortField,
        SalesDraftSortDirection
    >(
        filters.sort,
        (sortingFilter) => {
            if (sortingFilter) {
                setFilters((prev) => ({ ...prev, sort: sortingFilter }));
                sendEvent('drafts_list_sort_used', {
                    column: sortingFilter.field,
                    direction: sortingFilter.direction,
                });
            }
        },
        salesDraftSortFieldSchema.options,
    );

    return (
        <Page>
            <PageHeader
                icon={DraftIcon}
                subTitle={t('page_sub_title__drafts')}
                title={t('page_title__drafts')}
                actions={
                    transactionDraftAvailability !== 'UNAVAILABLE' && (
                        <Button
                            variant="primary"
                            onClick={() => {
                                void DialogStateManager.show(DraftOrderCreateDialog, {
                                    draftType: 'QUOTE',
                                    source: 'drafts_list',
                                });
                            }}
                            icon={PlusIcon}
                        >
                            <span>{t('top_navbar__new_draft')}</span>
                        </Button>
                    )
                }
            />
            <DraftListFilters />
            <ListView>
                <ListView.BodyContainer>
                    <DataTable
                        emptyLabel={t('empty_table__no_results')}
                        isLoading={isLoadingDraftList}
                        isFetchingNextPage={isFetchingDraftsListNextPage}
                        hasNextPage={hasNextPage}
                        onFetchNextPage={fetchNextPage}
                        data={data}
                        columns={columns}
                        manualSorting
                        sorting={sorting}
                        onSortingChange={sortChangedInInterface}
                    />
                </ListView.BodyContainer>
            </ListView>
        </Page>
    );
}
